import { useNavigate, useParams } from 'react-router';
import './detailsCircuit.scss';
import { useEffect, useState } from 'react';
import { fetchCircuit, fetchVouchers } from '../../data/slices/circuitSlice';
import UpdatedTable from '../../components/updated-table/StyledTable';
import useIsAdmin from '../../hooks/useIsAdmin';
import { 
    Box, 
    Button, 
    Stack,  // eslint-disable-line no-unused-vars
    Typography, 
    Chip, 
    Divider, 
    alpha,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent
} from '@mui/material';

import {
    West as WestIcon,
    DocumentScanner, 
    Print, 
    Update,
    Info,
    People,
    Description,
    CalendarMonth,
    TrendingUp,
    Receipt
} from '@mui/icons-material';

import { COLORS, dateFormatter, output, priceFormatter } from '../../settings/themeSettings';
import StyledTable from '../../components/table-component/StyledTable';
import { PDFViewer } from '@react-pdf/renderer';
import ClosureManagement from '../../components/ClosureManagement/ClosureManagement';
import EnveloppePDF from '../../components/EnveloppePDF/EnveloppePDF';
import { getStatusInfo, canModifyDossier } from '../../utils/statusHelper';

import _ from 'lodash';
import { notify } from '../../settings/toastSetting';
import dayjs from 'dayjs';

const DetailsCircuit = (props) => {
    const { id, type } = useParams();
    const isAdmin = useIsAdmin();

    const [loading, setLoading] = useState(false);
    const [circuit, setCircuit] = useState({
        hotels: [],
        excursions: [],
        transferts: [],
        locations: [],
        envelops: [],
        frais: [],
        groups: [],
        rooming: [],
    });
    const [tab, setTab] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [vouchers, setVouchers] = useState([]);
    const [forceUpdate, setForceUpdate] = useState(0); 
    const [enveloppeEuro, setEnveloppeEuro] = useState(false);
    const [openEnveloppePDF, setOpenEnveloppePDF] = useState(false);
    const navigateTo = useNavigate();

    const statusInfo = getStatusInfo(circuit);

    useEffect(() => {
        setLoading(true);

        fetchCircuit(id)
            .then((response) => {
                const resp = response.data;
                setLoading(false);
                output(resp);
                
                // Debug pour diagnostiquer les données récupérées
                console.log('[DEBUG] Données circuit brutes:', resp);
                console.log('[DEBUG] Chauffeur:', resp.chauffeur);
                console.log('[DEBUG] Guide:', resp.guide);
                console.log('[DEBUG] Chauffeur nomComplet:', resp.chauffeur?.nomComplet);
                console.log('[DEBUG] Guide nomComplet:', resp.guide?.nomComplet);

                switch (resp.type) {
                    case 'circuit':
                        setTabs(['Hebergements', 'Rooming', 'Excursions', 'Transferts', 'Locations', 'Enveloppe', 'Synthèse', 'Factures']);
                        break;
                    case 'sejour':
                        setTabs(['Hebergements', 'Rooming', 'Excursions', 'Transferts', 'Locations', 'Synthèse', 'Factures']);
                        break;
                    case 'excursion':
                        setTabs(['Excursions', 'Rooming', 'Enveloppe', 'Synthèse', 'Factures']);
                        break;
                    case 'transfert':
                        setTabs(['Transferts', 'Synthèse', 'Factures']);
                        break;
                    default:
                        setTabs(['Synthèse', 'Factures']);
                }

                // Conserver les IDs originaux avant transformation pour l'affichage
                resp.toClientId = resp.toClient?.id;
                resp.clientId = resp.client?.id;

                resp.toClient = resp.toClient?.libelle;
                resp.programme = resp.programme?.libelle;
                
                // Initialiser les données principales du circuit
                resp.chauffeur = resp.chauffeur?.nomComplet || null;
                resp.guide = resp.guide?.nomComplet || null;
                resp.vehicule = resp.vehicule ? `${resp.vehicule.marque || ''} - ${resp.vehicule.modele || ''} - ${resp.vehicule.matricule || ''}`.replace(/^ - | - $/g, '') : null;

                resp.hotels?.forEach((hotel) => {
                    hotel.hotel = hotel.hotel?.nom;
                });

                resp.excursions?.forEach((ex) => {
                    ex.chauffeur = ex.chauffeur?.nomComplet;
                    ex.guide = ex.guide?.nomComplet;
                    ex.excursion = ex.type?.libelle;
                });

                resp.transferts?.forEach((tr) => {
                    tr.chauffeur = tr.chauffeur?.nomComplet;
                    tr.guide = tr.guide?.nomComplet;
                    tr.vehicule = (tr.vehicule?.modele + ' - ' + (tr.vehicule?.matricule ?? ''));
                });

                resp.locations?.forEach((lc) => {
                    lc.chauffeur = lc.chauffeur?.nomComplet;
                    lc.guide = lc.guide?.nomComplet;
                    lc.vehicule = (lc.vehicule?.modele + ' - ' + (lc.vehicule?.matricule ?? ''));
                    lc.agence = lc.agence?.nom;
                });

                resp.pre_cloture = resp.pre_cloture || resp.preCloture || resp.precloture || false;
                resp.cloture_definitive = resp.cloture_definitive || resp.clotureDefinitive || false;

                setCircuit({
                    hotels: [],
                    excursions: [],
                    transferts: [],
                    locations: [],
                    envelops: [],
                    frais: [],
                    groups: [],
                    rooming: [],
                    ...resp
                });

                fetchVouchers(id).then((response) => {
                    const resp = response.data;
                    let _vouchers = resp.map((v) => {
                        v.prestataire = v.prestataire.nom;
                        return v;
                    });
                    setVouchers(_vouchers);
                }).catch(err => output(err));
            })
            .catch(err => {
                setLoading(false);
                output(err);
                notify("Erreur lors du chargement du dossier", "error");
            });
    }, [id, type, navigateTo, tabs.length, tab]);

    useEffect(() => {
        if (tabs.length > 0 && !tab) {
            setTab(tabs[0]);
        }
    }, [tabs, tab]);

    useEffect(() => {
        if (circuit?.id) {
            setForceUpdate(prev => prev + 1);
        }
    }, [circuit?.cloture_definitive, circuit?.pre_cloture]);

    const totalHotels = circuit?.hotels?.reduce((sum, h) => sum + (Number(h.montant) || 0), 0) || 0;
    const totalExcursions = circuit?.excursions?.reduce((sum, e) => sum + (Number(e.montant) || 0), 0) || 0;
    const totalTransferts = circuit?.transferts?.reduce((sum, t) => sum + (Number(t.montant) || 0), 0) || 0;
    const totalLocations = circuit?.locations?.reduce((sum, l) => sum + (Number(l.montant) || 0), 0) || 0;
    const totalEnveloppe = circuit?.envelops?.reduce((s, e) => s + (Number(e.depouillement) || 0), 0) || 0;
    const totalFraisAnnexes = circuit?.frais?.reduce((sum, f) => sum + (Number(f.montant) || 0), 0) || 0;
    const totalDeboursCalculated = totalHotels + totalExcursions + totalTransferts + totalLocations + totalEnveloppe + totalFraisAnnexes;

    const globalPax = Number(circuit?.pax || 1);
    const totalFactureCalculated = circuit?.factureCircuits?.reduce((sum, current) => {
        if (current.type === 'AV' || current.type === 'FP') return sum;
        const fraisSum = (current.frais || []).reduce((fSum, item) => {
            if (item.type === 'A' || item.nature === 'A' || item.isCanceled) return fSum;
            const pu = Number(item.montant || item.prixUnitaire || 0);
            const qte = Number(item.quantite) || 1;
            const itemPax = Number(item.nbrPax || item.pax || globalPax);
            return fSum + (pu * qte * itemPax);
        }, 0);
        return sum + fraisSum;
    }, 0) || 0;


    return loading ? (
        <div className="details-circuit-container">
            <div className="loader-container">
                <span className="loader"></span>
            </div>
        </div>
    ) : (
        !!circuit && (
            <div className="details-circuit-container" key={`circuit-${circuit.id}-${forceUpdate}`}>
                {/* Header Section */}
                <div className="page-header">
                    <div className="back-button" onClick={() => navigateTo(`/dossiers/${type}`)}>
                        <WestIcon />
                    </div>
                    <div className="header-content">
                        <Typography variant="h4">
                            Détails {type}
                            <span className="type-badge">{type?.toUpperCase()}</span>
                        </Typography>
                        <div className="breadcrumb">
                            Gestion AFT / {type} / {circuit.reference}
                        </div>
                    </div>
                    
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip 
                            label={`${statusInfo.icon} ${statusInfo.label}`} 
                            sx={{ 
                                bgcolor: alpha(statusInfo.color, 0.1), 
                                color: statusInfo.color,
                                fontWeight: 800,
                                border: `1px solid ${alpha(statusInfo.color, 0.3)}`,
                                px: 1,
                                height: 44,
                                borderRadius: '12px',
                                '& .MuiChip-label': { fontSize: '0.95rem' }
                            }} 
                        />
                    </Box>
                </div>

                {/* Summary Grid */}
                <div className="summary-grid">
                    {/* General & Financial Card */}
                    <div className="info-card">
                        <div className="card-title">
                            <Info /> Informations & Finance
                        </div>
                        
                        <div className="info-row">
                            <span className="label">Référence</span>
                            <span className="value">{circuit.reference}</span>
                        </div>

                        {circuit.nomClient && (
                            <div className="info-row">
                                <span className="label">Client Principal</span>
                                <span className="value">{circuit.nomClient}</span>
                            </div>
                        )}

                        {(type === 'circuit' || type === 'sejour') && (
                            <div className="info-row">
                                <span className="label">Programme</span>
                                <span className="value">{circuit.programme || "Libre"}</span>
                            </div>
                        )}

                        <div className="info-row">
                            <span className="label">Nombre de jours</span>
                            <span className="value">{circuit.nombreJour || "Non spécifié"}</span>
                        </div>

                        {circuit.toClient && (
                            <div className="info-row">
                                <span className="label">Partenaire / TO</span>
                                <span className="value">{circuit.toClient}</span>
                            </div>
                        )}

                        {!circuit.nomClient && !circuit.toClient && (
                            <div className="info-row">
                                <span className="label">Client</span>
                                <span className="value" style={{ fontStyle: 'italic', color: '#888' }}>Nom non renseigné</span>
                            </div>
                        )}

                        <Divider sx={{ my: 1 }} />

                        <div className="info-row">
                            <span className="label">Total Débours</span>
                            <span className="value price">
                                {priceFormatter(totalDeboursCalculated)}
                            </span>
                        </div>

                        <div className="info-row">
                            <span className="label">Total Facturé</span>
                            <span className="value">
                                {priceFormatter(totalFactureCalculated)}
                            </span>
                        </div>

                        <div className="info-row">
                            <span className="label">Marge Nette</span>
                            <span className="value" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>
                                {priceFormatter(totalFactureCalculated - totalDeboursCalculated)}
                            </span>
                        </div>
                    </div>

                    {/* Logistics Card */}
                    <div className="info-card">
                        <div className="card-title">
                            <People /> Équipe & Logistique
                        </div>

                        <div className="info-row">
                            <span className="label">Chauffeur Accomp.</span>
                            <span className="value">{circuit?.chauffeur || 'Aucun chauffeur assigné'}</span>
                        </div>

                        <div className="info-row">
                            <span className="label">Guide Certifié</span>
                            <span className="value">{circuit?.guide || 'Aucun guide assigné'}</span>
                        </div>

                        <Divider sx={{ my: 1 }} />

                        <div className="info-row">
                            <span className="label">Véhicule</span>
                            <span className="value">
                                {circuit.vehicule || 'Non spécifié'}
                            </span>
                        </div>

                        {(type === 'circuit' || type === 'sejour') && (
                            <>
                                <div className="info-row">
                                    <span className="label">Pax Total</span>
                                    <span className="value">{circuit.pax || 0} Clients</span>
                                </div>
                                <Divider sx={{ my: 1 }} />
                                <div className="card-title" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                                    <CalendarMonth /> Vols de Transit
                                </div>
                                <div className="info-row">
                                    <span className="label">Arrivée prévue</span>
                                    <span className="value">
                                        {dateFormatter(circuit?.dateVolArrivee)}
                                    </span>
                                </div>
                                {circuit?.compagnieVolArrivee && (
                                    <div className="info-row">
                                        <span className="label">Vol Arrivée</span>
                                        <span className="value">{circuit.compagnieVolArrivee} {circuit.numeroVolArrivee ? `(${circuit.numeroVolArrivee})` : ''}</span>
                                    </div>
                                )}
                                <div className="info-row">
                                    <span className="label">Départ prévu</span>
                                    <span className="value">
                                        {dateFormatter(circuit?.dateVolDepart)}
                                    </span>
                                </div>
                                {circuit?.compagnieVolDepart && (
                                    <div className="info-row">
                                        <span className="label">Vol Départ</span>
                                        <span className="value">{circuit.compagnieVolDepart} {circuit.numeroVolDepart ? `(${circuit.numeroVolDepart})` : ''}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Quick Actions Card */}
                    <div className="info-card actions-card">
                        <div className="card-title">
                            <TrendingUp /> Actions & Clôture
                        </div>

                        <div className="btn-stack" style={{ flex: 1 }}>
                            {/* Section Factures ultra-visible si elles existent */}
                            {circuit.factureCircuits?.length > 0 && (
                                <Box sx={{ 
                                    mb: 1.5, 
                                    p: 1.5, 
                                    bgcolor: alpha(COLORS.secondary, 0.05), 
                                    borderRadius: '10px',
                                    border: `1px solid ${alpha(COLORS.secondary, 0.2)}`
                                }}>
                                    <Typography variant="caption" sx={{ color: COLORS.secondary, fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 1, textTransform: 'uppercase' }}>
                                        <Receipt sx={{ fontSize: '1rem' }} /> Factures Émises
                                    </Typography>
                                    <Stack spacing={0.8}>
                                        {[...circuit.factureCircuits]
                                            .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
                                            .map((facture, fIdx) => (
                                                <Button 
                                                    key={fIdx}
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<Print />}
                                                    onClick={() => {
                                                        navigateTo(`/dossiers/${type}/${id}/factures/${facture.id}`);
                                                    }}
                                                    sx={{ 
                                                        justifyContent: 'space-between',
                                                        bgcolor: 'white',
                                                        color: COLORS.secondary,
                                                        border: `1px solid ${alpha(COLORS.secondary, 0.3)}`,
                                                        '&:hover': {
                                                            bgcolor: alpha(COLORS.secondary, 0.1),
                                                            borderColor: COLORS.secondary,
                                                        },
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        py: 0.8,
                                                        px: 1.5,
                                                        textTransform: 'none',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="caption" fontWeight={900}>
                                                            {facture.numero || facture.numeroFacture}
                                                        </Typography>
                                                    </Box>
                                                    <Print sx={{ fontSize: '0.9rem', opacity: 0.8 }} />
                                                </Button>
                                            ))
                                        }
                                    </Stack>
                                </Box>
                            )}

                            <Button className="action-btn" onClick={() => navigateTo(`/dossiers/${type}/${id}/pdf`)}>
                                <Print /> Imprimer Fiche Dossier Circuit
                            </Button>

                            {!circuit.cloture_definitive && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: 'rgba(0,0,0,0.05)', p: 1, borderRadius: 2 }}>
                                    <Button className="action-btn" onClick={() => setOpenEnveloppePDF(true)} sx={{ margin: 0 }}>
                                        <Description /> Enveloppe Frais
                                    </Button>
                                </Box>
                            )}

                            <Button className="action-btn" onClick={() => navigateTo(`/facturation/nouvelle/dossier/${type}/${id}`)}>
                                <DocumentScanner /> Facturation
                            </Button>

                            {canModifyDossier(circuit) && isAdmin && (
                                <Button className="action-btn primary" onClick={() => navigateTo(`/dossiers/${type}/edit/${id}`)}>
                                    <Update /> Modifier
                                </Button>
                            )}
                        </div>

                        <Box sx={{ background: 'rgba(0,0,0,0.15)', p: 1.5, borderRadius: '12px', mt: 'auto' }}>
                            <ClosureManagement
                                dossier={circuit}
                                type={type}
                                onStateChange={(updatedDossier) => {
                                    setCircuit(prev => ({ ...prev, ...updatedDossier }));
                                    setTimeout(() => window.location.reload(), 500);
                                }}
                            />
                        </Box>
                    </div>
                </div>

                {/* Tabs Section */}
                <div className="tabs">
                    {tabs.map((_tab, idx) => (
                        <div 
                            key={idx} 
                            className={tab === _tab ? "tab active" : "tab"} 
                            onClick={() => setTab(_tab)}
                        >
                            {_tab}
                        </div>
                    ))}
                </div>

                <div className="tab-content">
                    {(() => {
                        switch (tab) {
                            case 'Hebergements':
                                return (
                                    <>
                                        <UpdatedTable 
                                            rows={circuit.hotels ? _.sortBy([...circuit.hotels], (i) => new Date(i.dateDebut)) : []} 
                                            head={['dateDebut', 'dateFin', 'nuitee', 'hotel', 'prixUnitaire', 'typeChambre', 'regime', 'pax', 'montant', 'categorieChambre']} 
                                            totals={['montant']} 
                                        />
                                        <div className="vouchers-section">
                                            <div className="section-header">Vouchers Émis</div>
                                            <StyledTable 
                                                rows={vouchers} 
                                                head={['numero', 'prestataire']} 
                                                headLabels={{ numero: 'N° Voucher', prestataire: 'Hôtel / Prestataire' }}
                                                onOptionClicked={(vid) => navigateTo(`/dossiers/${type}/${id}/voucher/${vid}`)} 
                                            />
                                        </div>
                                    </>
                                );
                            case 'Rooming':
                                return (
                                    <UpdatedTable 
                                        rows={circuit.isGroup ? (circuit.groups || []) : (circuit.rooming || [])} 
                                        head={circuit.isGroup ? ['libelle', 'pax'] : ['civilite', 'nom', 'prenom']} 
                                    />
                                );
                            case 'Excursions':
                                return <UpdatedTable rows={circuit.excursions || []} head={['date', 'heure', 'excursion', 'observation', 'chauffeur', 'guide', 'pax', 'prixUnitaire', 'montant']} totals={['montant']} />;
                            case 'Transferts':
                                return <UpdatedTable rows={circuit.transferts || []} head={['date', 'heure', 'lieuDepart', 'lieuArrivee', 'vehicule', 'chauffeur', 'guide', 'pax', 'prixUnitaire', 'montant']} totals={['montant']} />;
                            case 'Locations':
                                return <UpdatedTable rows={circuit.locations || []} head={['agence', 'dateDebut', 'dateFin', 'nombreJour', 'prixUnitaire', 'vehicule', 'chauffeur', 'guide', 'montant']} totals={['montant']} />;
                            case 'Enveloppe':
                                return <UpdatedTable rows={circuit.envelops || []} head={['frais', 'pax', 'prixUnitaire', 'montant', 'depouillement']} totals={['montant', 'depouillement']} />;
                            case 'Synthèse':
                                return <UpdatedTable rows={circuit.frais || []} head={['frais', 'montant']} totals={['montant']} />;
                            case 'Factures':
                                return <UpdatedTable 
                                    rows={circuit.factureCircuits || []} 
                                    head={['numero', 'date', 'type', 'montant']} 
                                    headLabels={{ numero: 'N° Facture', date: 'Date Émission', type: 'Type', montant: 'Montant' }}
                                    customRenderers={{
                                        'montant': (val, row) => {
                                            const factureSum = (row.frais || []).reduce((fSum, item) => {
                                                if (item.type === 'A' || item.nature === 'A' || item.isCanceled) return fSum;
                                                const pu = Number(item.montant || item.prixUnitaire || 0);
                                                const qte = Number(item.quantite) || 1;
                                                const itemPax = Number(item.nbrPax || item.pax || globalPax);
                                                return fSum + (pu * qte * itemPax);
                                            }, 0);
                                            return priceFormatter(factureSum);
                                        },
                                        'date': (val) => dateFormatter(val)
                                    }}
                                    onOptionClicked={(fid) => navigateTo(`/dossiers/${type}/${id}/factures/${fid}`)}
                                />;
                            default:
                                return <Box sx={{ p: 4, textAlign: 'center', color: COLORS.gray[400] }}>Sélectionnez un onglet pour voir les détails</Box>;
                        }
                    })()}
                </div>
                
                {/* Modal pour afficher le PDF de l'enveloppe */}
                <Dialog 
                    open={openEnveloppePDF} 
                onClose={() => setOpenEnveloppePDF(false)} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{ style: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Aperçu de l'Enveloppe</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch size="small" checked={enveloppeEuro} onChange={(e) => setEnveloppeEuro(e.target.checked)} />}
                            label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>Afficher en EUR</Typography>}
                            sx={{ m: 0 }}
                        />
                        <Button onClick={() => setOpenEnveloppePDF(false)} color="error" variant="outlined" size="small">Fermer</Button>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
                    <PDFViewer style={{ width: '100%', height: '100%', border: 'none' }}>
                        <EnveloppePDF dossier={circuit} euro={enveloppeEuro} />
                    </PDFViewer>
                </DialogContent>
            </Dialog>
            </div>
        )
    );
};

export default DetailsCircuit;
